import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { itemLabel } from "./accountsModel";

export const useAccountsData = (userId) => {
  const [accounts, setAccounts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accounts/${userId}`);
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/${userId}`);
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  }, [userId]);

  useEffect(() => { fetchAccounts(); fetchLeads(); }, [fetchAccounts, fetchLeads]);

  const cleanupBlank = async () => {
    setCleaningUp(true);
    try {
      const res = await axios.delete(`${API}/accounts/${userId}/cleanup-blank`);
      const count = res.data.deletedCount || 0;
      if (count > 0) {
        toast.success(`Removed ${count} blank account${count > 1 ? "s" : ""}`);
        fetchAccounts();
      } else {
        toast.info("No blank accounts found");
      }
    } catch (e) {
      toast.error("Cleanup failed");
    } finally {
      setCleaningUp(false);
    }
  };

  const addItem = async (viewMode, form) => {
    setSubmitting(true);
    try {
      if (viewMode === "accounts") {
        const res = await axios.post(`${API}/accounts`, {
          userId, companyName: form.companyName, industry: form.industry, persona: form.persona || "Decision Maker",
        });
        setAccounts((prev) => [res.data, ...prev]);
      } else {
        const res = await axios.post(`${API}/leads`, {
          userId, companyName: form.companyName, contactName: form.contactName, contactTitle: form.contactTitle,
          contactEmail: form.contactEmail, industry: form.industry, status: form.status || "new", source: "manual",
        });
        setLeads((prev) => [res.data, ...prev]);
      }
      toast.success(`${itemLabel(viewMode)} added successfully`);
      return true;
    } catch (error) {
      console.error("Error adding:", error);
      toast.error(`Failed to add ${itemLabel(viewMode).toLowerCase()}`);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const deleteItem = async (viewMode, item) => {
    setSubmitting(true);
    try {
      if (viewMode === "accounts") {
        await axios.delete(`${API}/accounts/${item.accountId}`);
        setAccounts((prev) => prev.filter((a) => a.accountId !== item.accountId));
      } else {
        await axios.delete(`${API}/leads/${item.leadId}`);
        setLeads((prev) => prev.filter((l) => l.leadId !== item.leadId));
      }
      toast.success(`${itemLabel(viewMode)} deleted`);
      return true;
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error(`Failed to delete ${itemLabel(viewMode).toLowerCase()}`);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const updateLead = (updated) => setLeads((prev) => prev.map((l) => (l.leadId === updated.leadId ? updated : l)));
  const refresh = useCallback(() => { fetchAccounts(); fetchLeads(); }, [fetchAccounts, fetchLeads]);

  return { accounts, leads, loading, submitting, cleaningUp, cleanupBlank, addItem, deleteItem, updateLead, refresh };
};
